// Client-side store for the request-scoped session token issued by
// `requestGetAuth` after a successful validationCode verification.
// Mirrors `portalSession` but lives in a separate slot. The two can
// coexist when a patient opens an emailed request on a device that is
// already signed in to the portal, so request calls must explicitly
// prefer this token. Keeping them separate also prevents a patient
// logout from clobbering an in-progress request session and vice versa.

const TOKEN_KEY = 'healthcom-schedule.requestSession.token';
const REQUEST_ID_KEY = 'healthcom-schedule.requestSession.requestId';
const EXPIRES_KEY = 'healthcom-schedule.requestSession.expiresAt';

export const REQUEST_SESSION_EXPIRED_EVENT = 'healthcom-schedule:request-session-expired';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const getStatusCode = (err: unknown): number | null => {
  const error = asRecord(err);
  const response = asRecord(error?.response);
  const data = asRecord(response?.data);
  const statusCode = data?.statusCode ?? response?.status ?? error?.status;

  return typeof statusCode === 'number' ? statusCode : null;
};

export const requestSession = {
  getToken(): string | null {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },

  setToken(token: string, requestId: string, expiresAt?: string) {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(REQUEST_ID_KEY, requestId);
      if (expiresAt) sessionStorage.setItem(EXPIRES_KEY, expiresAt);
    } catch {
      /* storage blocked */
    }
  },

  getRequestId(): string | null {
    try {
      return sessionStorage.getItem(REQUEST_ID_KEY);
    } catch {
      return null;
    }
  },

  getExpiresAt(): string | null {
    try {
      return sessionStorage.getItem(EXPIRES_KEY);
    } catch {
      return null;
    }
  },

  clear() {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(REQUEST_ID_KEY);
      sessionStorage.removeItem(EXPIRES_KEY);
    } catch {
      /* ignore */
    }
  },

  isLikelyValid(): boolean {
    const token = this.getToken();

    if (!token) return false;

    const expiresAt = this.getExpiresAt();

    if (!expiresAt) return true;

    const expiresAtMs = Date.parse(expiresAt);

    return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();
  },

  recoverFromUnauthorized(err: unknown): boolean {
    if (!this.getToken() || getStatusCode(err) !== 401) {
      return false;
    }

    this.clear();

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(REQUEST_SESSION_EXPIRED_EVENT));
    }

    return true;
  },
};
